import {
  AppWindow,
  Blocks,
  Briefcase,
  Calendar,
  Cloud,
  Database,
  FolderTree,
  HardDrive,
  Loader2,
  Mail,
  Network,
  ShieldCheck,
  Sparkle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import sharepointIcon from 'assets/images/sharepoint.svg';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { cn } from 'lib/utils';
import type { IntegrationProviders } from 'services/integration-service';

interface IntegrationVisualConfig {
  icon?: string;
  accentClassName: string;
  glyph: JSX.Element;
}

const integrationVisuals: Record<string, IntegrationVisualConfig> = {
  'google-drive': {
    icon: gdriveIcon,
    accentClassName: 'from-blue-500/15 via-sky-500/10 to-emerald-500/15',
    glyph: <HardDrive className="h-4 w-4 text-blue-600 dark:text-blue-300" />,
  },
  gmail: {
    accentClassName: 'from-red-500/15 via-orange-500/10 to-yellow-500/15',
    glyph: <Mail className="h-4 w-4 text-red-500 dark:text-red-300" />,
  },
  'outlook-email': {
    accentClassName: 'from-sky-500/15 via-blue-500/10 to-cyan-500/15',
    glyph: <Mail className="h-4 w-4 text-sky-600 dark:text-sky-300" />,
  },
  'outlook-calendar': {
    accentClassName: 'from-cyan-500/15 via-blue-500/10 to-indigo-500/15',
    glyph: <Calendar className="h-4 w-4 text-cyan-600 dark:text-cyan-300" />,
  },
  onedrive: {
    icon: onedriveIcon,
    accentClassName: 'from-sky-500/15 via-blue-500/10 to-indigo-500/15',
    glyph: <Cloud className="h-4 w-4 text-sky-600 dark:text-sky-300" />,
  },
  'microsoft-sharepoint': {
    icon: sharepointIcon,
    accentClassName: 'from-emerald-500/15 via-teal-500/10 to-cyan-500/15',
    glyph: <Briefcase className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />,
  },
  jira: {
    accentClassName: 'from-blue-500/15 via-indigo-500/10 to-violet-500/15',
    glyph: <Blocks className="h-4 w-4 text-blue-600 dark:text-blue-300" />,
  },
  confluence: {
    accentClassName: 'from-indigo-500/15 via-blue-500/10 to-sky-500/15',
    glyph: <Network className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />,
  },
  airtable: {
    accentClassName: 'from-amber-500/15 via-orange-500/10 to-red-500/15',
    glyph: <Database className="h-4 w-4 text-amber-600 dark:text-amber-300" />,
  },
  asana: {
    accentClassName: 'from-rose-500/15 via-pink-500/10 to-orange-500/15',
    glyph: <Sparkle className="h-4 w-4 text-rose-600 dark:text-rose-300" />,
  },
};

const providerAliases: Record<string, string> = {
  google: 'google-drive',
  googledrive: 'google-drive',
  google_drive: 'google-drive',
  'google drive': 'google-drive',
  sharepoint: 'microsoft-sharepoint',
  microsoft: 'microsoft-sharepoint',
  microsoft_sharepoint: 'microsoft-sharepoint',
  'microsoft sharepoint': 'microsoft-sharepoint',
  'microsoft-sharepoint': 'microsoft-sharepoint',
  one_drive: 'onedrive',
  'one drive': 'onedrive',
  outlook: 'outlook-email',
  outlook_mail: 'outlook-email',
  'outlook mail': 'outlook-email',
};

const slugify = (value?: string) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[_\s]+/g, '-');

export const resolveProviderKey = (provider: IntegrationProviders) => {
  const candidates = [
    provider.provider_id,
    provider.integration_type,
    provider.display_name,
    provider.provider_family,
  ]
    .filter(Boolean)
    .map((value) => slugify(value));

  for (const candidate of candidates) {
    if (integrationVisuals[candidate]) {
      return candidate;
    }

    const alias = providerAliases[candidate] || providerAliases[candidate.replace(/-/g, '_')];
    if (alias) {
      return alias;
    }
  }

  return candidates[0] || 'integration';
};

const getVisualConfig = (providerId: string): IntegrationVisualConfig => {
  return (
    integrationVisuals[providerId] || {
      accentClassName: 'from-slate-500/15 via-zinc-500/10 to-stone-500/15',
      glyph: <AppWindow className="h-4 w-4 text-slate-600 dark:text-slate-300" />,
    }
  );
};

const getDescription = (
  provider: IntegrationProviders,
  translate: (key: string, options?: Record<string, unknown>) => string
) => {
  const capabilities = provider.supported_capabilities?.length
    ? provider.supported_capabilities.map((capability) => capability.replace(/[_-]/g, ' '))
    : [];

  if (capabilities.length > 0) {
    const sentence = capabilities.slice(0, 3).join(', ');
    return (
      translate('INTEGRATION_SUPPORTS', {
        name: provider.display_name,
        capabilities: sentence,
        defaultValue: '{{name}} supports {{capabilities}}.',
      }) || `${provider.display_name} supports ${sentence}.`
    );
  }

  if (provider.provider_family) {
    const family = provider.provider_family.replace(/[_-]/g, ' ');
    return (
      translate('INTEGRATION_BELONGS_TO_FAMILY', {
        name: provider.display_name,
        family,
        defaultValue: '{{name}} belongs to the {{family}} integration family.',
      }) || `${provider.display_name} belongs to the ${family} integration family.`
    );
  }

  return (
    translate('INTEGRATION_CONNECT_DESCRIPTION', {
      name: provider.display_name,
      defaultValue: 'Connect {{name}} to use it as an external data source inside your workspace.',
    }) || `Connect ${provider.display_name} to use it as an external data source inside your workspace.`
  );
};

export interface IntegrationCardProps {
  readonly provider: IntegrationProviders;
  readonly isBusy: boolean;
  readonly isConnected: boolean;
  readonly canBrowse: boolean;
  readonly onConnect: (providerId: string) => void;
  readonly onOpenBrowser: (provider: IntegrationProviders) => void;
  readonly onDisconnect: (provider: IntegrationProviders) => void;
}

function IntegrationCard({
  provider,
  isBusy,
  isConnected,
  canBrowse,
  onConnect,
  onOpenBrowser,
  onDisconnect,
}: IntegrationCardProps) {
  const { t } = useTranslation();
  const providerId = resolveProviderKey(provider);
  const visual = getVisualConfig(providerId);
  const isEnabled = provider.status === 'enabled' || provider.status === 'active';
  const isComingSoon = !isConnected && !isEnabled;
  const description = getDescription(provider, t);
  const buttonLabel = isConnected
    ? t('DISCONNECT') || 'Disconnect'
    : isEnabled
      ? t('CONNECT') || 'Connect'
      : t('COMING_SOON') || 'Coming soon';
  const buttonVariant = isConnected ? 'destructive' : isEnabled ? 'outline' : 'secondary';
  const integrationLabel = t('INTEGRATION') || 'Integration';
  const familyLabel = provider.provider_family || integrationLabel;
  const browseLabel = provider.resource_browse_supported
    ? t('BROWSABLE') || 'Browsable'
    : t('CONNECTION_ONLY') || 'Connection only';

  return (
    <Card
      className={cn(
        'group flex w-full min-w-0 flex-col gap-3 rounded-lg border border-grey-50 bg-white p-4 shadow-none transition-colors sm:p-6',
        'dark:border-border dark:bg-card dark:hover:bg-muted/40'
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border-0 bg-grey-100',
            'bg-gradient-to-br dark:bg-white/10',
            visual.accentClassName
          )}
        >
          {visual.icon ? (
            <img
              alt={provider.display_name}
              className="h-5 w-5 object-contain"
              src={visual.icon}
            />
          ) : (
            visual.glyph
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {canBrowse ? (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-md border-[#e7e7e7] dark:border-border"
              disabled={isBusy}
              onClick={() => onOpenBrowser(provider)}
              type="button"
            >
              <FolderTree className="h-4 w-4" />
              <span className="sr-only">
                {t('BROWSE_INTEGRATION', {
                  name: provider.display_name,
                  defaultValue: 'Browse {{name}}',
                }) || `Browse ${provider.display_name}`}
              </span>
            </Button>
          ) : null}
          <Button
            className={cn(
              'h-8 shrink-0 rounded-md px-3 text-xs font-medium sm:text-sm',
              isComingSoon &&
              'border-0 bg-grey-50 text-grey-500 opacity-100 hover:bg-grey-50 dark:bg-white/10 dark:text-muted-foreground dark:hover:bg-white/10'
            )}
            size="sm"
            variant={buttonVariant}
            disabled={isBusy || isComingSoon}
            type="button"
            onClick={() => {
              if (isConnected) {
                onDisconnect(provider);
                return;
              }

              if (isEnabled) {
                onConnect(provider.provider_id);
              }
            }}
          >
            {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : buttonLabel}
          </Button>
        </div>
      </div>

      <h3
        className="min-w-0 w-full truncate text-sm font-semibold leading-[1.2] text-grey-700 2xl:text-base dark:text-foreground"
        title={provider.display_name}
      >
        {provider.display_name}
      </h3>

      <p
        className="line-clamp-3 min-w-0 w-full text-xs font-normal leading-[1.6] text-grey-700 2xl:text-sm dark:text-muted-foreground"
        title={description}
      >
        {description}
      </p>

      <div className="flex min-w-0 flex-wrap items-center gap-[7px] gap-y-2">
        {isConnected ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded bg-[#ECFDF3] px-2 py-1 text-xs font-semibold leading-[1.2] text-[#079455] dark:bg-success-200 dark:text-success-800">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t('CONNECTED') || 'Connected'}
          </span>
        ) : isComingSoon ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded bg-grey-50 px-2 py-1 text-xs font-semibold leading-[1.2] text-grey-500 dark:bg-white/10 dark:text-muted-foreground">
            {t('COMING_SOON') || 'Coming soon'}
          </span>
        ) : null}

        <span className="inline-block min-w-0 max-w-full truncate rounded bg-grey-50 px-2 py-1 text-xs font-semibold leading-[1.2] text-grey-700 dark:bg-white/10 dark:text-foreground">
          {familyLabel}
        </span>

        <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded bg-grey-50 px-2 py-1 text-xs font-semibold leading-[1.2] text-grey-700 dark:bg-white/10 dark:text-foreground">
          {provider.resource_browse_supported ? (
            <ShieldCheck className="h-3.5 w-3.5" />
          ) : (
            <Network className="h-3.5 w-3.5" />
          )}
          {browseLabel}
        </span>
      </div>
    </Card>
  );
}

export default IntegrationCard;
